import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift, Crown, Check, AlertCircle, ArrowLeft, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

export default function MembershipReserve() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [card, setCard] = useState({ number: "4242 4242 4242 4242", holder: "", exp: "12/28", cvc: "123" });

  useEffect(() => {
    api.get("/membership/early-reserve/status").then((r) => setStatus(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError("Debes aceptar la cláusula de no-reembolso"); return; }
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/membership/early-reserve");
      setSuccess(data);
      await refresh();
      setTimeout(() => nav("/app/membresia"), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo procesar la reserva");
    } finally {
      setLoading(false);
    }
  };

  if (!status) return <div className="container-app py-16"><p className="text-muted-foreground">Cargando…</p></div>;

  if (user?.is_embajador_pionero || user?.plan === "founder") {
    return (
      <div className="container-app py-16 max-w-xl text-center" data-testid="reserve-already-done">
        <div className="panel p-8">
          <Crown size={32} className="text-amber-400 mx-auto" />
          <h1 className="serif text-2xl mt-4">Ya tienes tu lugar asegurado</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {user.is_founder ? `Eres Fundador N°${user.founder_number}.` : `Eres Pionero N°${user.early_reserve_number}.`}
          </p>
          <Link to="/app/membresia" className="btn-primary mt-6 inline-flex">Ir a mi membresía</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container-app py-20 max-w-xl text-center" data-testid="reserve-success">
        <div className="panel p-10 ring-2 ring-amber-400/40">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center">
            <Gift size={28} className="text-amber-400" />
          </div>
          <h1 className="serif text-3xl mt-6">¡Bienvenido a la familia pionera!</h1>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/15 border border-amber-400/40">
            <Crown size={16} className="text-amber-400" />
            <span className="text-amber-300 font-bold tracking-wider">PIONERO N°{success.reserve_number}</span>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">{success.message}</p>
          <p className="mt-6 text-xs text-muted-foreground">Redirigiendo a tu membresía…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-12 max-w-4xl" data-testid="reserve-page">
      <Link to="/planes" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> Volver a planes
      </Link>
      <div className="grid md:grid-cols-5 gap-8">
        <aside className="md:col-span-2 space-y-4">
          <div className="panel p-6 ring-2 ring-amber-400/30">
            <div className="inline-flex items-center gap-1.5 text-amber-300 text-xs font-bold tracking-wider mb-2">
              <Crown size={14} /> Reserva pre-lanzamiento
            </div>
            <h2 className="serif text-2xl">Pionero Fundador</h2>
            <div className="serif text-4xl mt-3 text-amber-300">{CLP(status.reserve_amount_clp)}</div>
            <div className="text-xs text-muted-foreground mt-1">único pago · no reembolsable</div>

            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-start gap-2"><Check size={14} className="text-amber-400 mt-0.5 shrink-0" />Cupo Fundador <span className="text-foreground">garantizado</span> entre los 50 totales</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-amber-400 mt-0.5 shrink-0" />Insignia <span className="text-amber-300 font-medium">Pionero N°X</span> en tu perfil</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-amber-400 mt-0.5 shrink-0" />Los {CLP(status.reserve_amount_clp)} se descuentan de tu anualidad Fundador ({CLP(status.founder_annual_price_clp)})</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-amber-400 mt-0.5 shrink-0" />Acceso al grupo cerrado de fundadores pre-lanzamiento</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-amber-400 mt-0.5 shrink-0" />Comisión 5% bloqueada de por vida al activar membresía</li>
            </ul>
          </div>
          <div className="panel p-4 text-xs text-muted-foreground flex items-start gap-2">
            <Lock size={14} className="mt-0.5 text-[hsl(var(--accent))]" />
            <div>Reserva simulada · sin cobro real en esta etapa MVP. En producción se integra Flow.cl / Webpay.</div>
          </div>
          <div className="text-xs text-muted-foreground">
            Cupos restantes <span className="text-amber-300 font-medium">{status.remaining}</span> / {status.founder_cap}
          </div>
        </aside>

        <form onSubmit={submit} className="md:col-span-3 panel p-8" data-testid="reserve-form">
          <h1 className="serif text-2xl">Confirmar reserva</h1>
          <p className="mt-1 text-sm text-muted-foreground">Una sola tarjeta, único cobro de {CLP(status.reserve_amount_clp)}.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs text-muted-foreground">Número de tarjeta</label>
              <input value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm" data-testid="reserve-card-number" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Titular</label>
              <input value={card.holder} onChange={(e) => setCard({ ...card, holder: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm" placeholder="Nombre" data-testid="reserve-holder" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Vencimiento</label>
                <input value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm" data-testid="reserve-exp" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">CVC</label>
                <input value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm" data-testid="reserve-cvc" />
              </div>
            </div>
          </div>

          <label className="mt-6 panel p-4 bg-amber-400/5 border border-amber-400/30 flex items-start gap-3 text-xs cursor-pointer" data-testid="accept-no-refund">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 accent-amber-400" />
            <span>
              Entiendo y acepto que esta reserva es <span className="text-amber-300 font-medium">NO REEMBOLSABLE</span> y que los
              {" "}{CLP(status.reserve_amount_clp)}{" "}se acreditarán íntegramente a mi suscripción anual Fundador al lanzamiento
              de la plataforma. He leído los <Link to="/terminos" target="_blank" className="underline">Términos de Servicio</Link>.
            </span>
          </label>

          {error && (
            <div className="mt-4 flex items-start gap-2 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-md p-3" data-testid="reserve-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <button disabled={loading} className="mt-6 w-full btn-primary bg-amber-500 hover:bg-amber-400 text-amber-950 disabled:opacity-50" data-testid="reserve-submit">
            {loading ? "Procesando…" : `Pagar ${CLP(status.reserve_amount_clp)} y asegurar mi cupo`}
          </button>
        </form>
      </div>
    </div>
  );
}
